import * as yup from 'yup';
import { useMatch } from 'react-router';
import { useTranslation } from 'react-i18next';

import { AccessKeyActionType } from '~/common/enums';
import { type AvailableAccessKeyInstance } from '@/types/trpc/accessKeys';
import { type CrmAutoCompleteOption } from '~/common/types';
import Routes from '@/containers/App/Router/Routes';
import { trpc } from '@/config/trpc';
import useDayjs from '@/shared/hooks/useDayjs';

export type AccessKeyActionFormikValues = {
  accessKeys: AvailableAccessKeyInstance[];
  borrower: CrmAutoCompleteOption | null;
  fromDate?: string | null;
  untilDate?: string | null;
  actionType: AccessKeyActionType;
  caseId?: string | null;
  remark?: string | null;
  shouldInformCoOwner: boolean;
  shouldInformBorrower: boolean;
};

const useAccessKeyActionFormikConfig = (defaultActionType?: AccessKeyActionType) => {
  const { t } = useTranslation();
  const { dayjs } = useDayjs();

  const defaultCaseId = useMatch(Routes.CASE_DETAIL)?.params?.caseId;

  const { data: accessKeySettings, isLoading } = trpc.accessKeys.settings.get.useQuery(undefined, {
    retry: 0,
  });

  const initialValues: AccessKeyActionFormikValues = {
    accessKeys: [],
    actionType: defaultActionType || AccessKeyActionType.Loan,
    borrower: null,
    caseId: defaultCaseId ?? null,
    fromDate:
      defaultActionType && defaultActionType !== AccessKeyActionType.Loan
        ? null
        : dayjs().format('YYYY-MM-DD'),
    remark: '',
    shouldInformBorrower: true,
    shouldInformCoOwner: true,
    untilDate:
      defaultActionType && defaultActionType !== AccessKeyActionType.Loan
        ? null
        : accessKeySettings?.lendingPeriodInDays
        ? dayjs().add(accessKeySettings.lendingPeriodInDays, 'days').format('YYYY-MM-DD')
        : null,
  };

  const validationSchema = yup.object({
    accessKeys: yup
      .array()
      .min(1, t('errors:fieldIsRequired'))
      .of(
        yup.object({
          accessKeyId: yup.string().required(),
          accessKeyInstanceId: yup.string().required(),
        }),
      ),
    borrower: yup.object().when('actionType', {
      is: (actionType: AccessKeyActionType) =>
        [AccessKeyActionType.Loan, AccessKeyActionType.Reservation].includes(actionType),
      otherwise: yup.object().nullable(),
      then: yup
        .object()
        .typeError(t('errors:fieldIsRequired'))
        .required(t('errors:fieldIsRequired')),
    }),
    fromDate: yup.date().when('actionType', {
      is: (actionType: AccessKeyActionType) =>
        [AccessKeyActionType.Loan, AccessKeyActionType.Reservation].includes(actionType),
      otherwise: yup.date().nullable(),
      then: yup.date().typeError(t('errors:fieldIsRequired')).required(t('errors:fieldIsRequired')),
    }),
    remark: yup.string().max(255).nullable(),
    untilDate: yup.date().when('actionType', {
      is: (actionType: AccessKeyActionType) =>
        [AccessKeyActionType.Loan, AccessKeyActionType.Reservation].includes(actionType),
      otherwise: yup.date().nullable(),
      then: yup.date().typeError(t('errors:fieldIsRequired')).required(t('errors:fieldIsRequired')),
    }),
  });

  return {
    initialValues,
    isLoading,
    validationSchema,
  };
};

export default useAccessKeyActionFormikConfig;
